import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui';
import { Key, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores';

export function TwoFactorBackup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setTokens, setUser } = useAuthStore();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tempToken, setTempToken] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('tempToken');
    if (!token) {
      navigate('/login');
      return;
    }
    setTempToken(token);
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tempToken || code.length < 8) {
      toast.error('请输入有效的备用码');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/auth/login/2fa', {
        tempToken,
        code,
      });

      if (response.data.success) {
        const { user, access_token, refresh_token, expires_in } = response.data.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        setTokens({ access_token, refresh_token, expires_in: expires_in || 3600 });
        setUser(user);
        toast.success('登录成功');
        navigate('/');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || '备用码无效或已使用');
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
            <Key className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
          </div>
          <CardTitle className="text-2xl">使用备用码</CardTitle>
          <CardDescription>
            输入您在设置双因素认证时保存的备用码
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="text"
                placeholder="输入备用码"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="text-center text-xl tracking-wider font-mono"
                autoFocus
              />
              <p className="text-xs text-muted-foreground text-center">
                备用码区分大小写，每个备用码只能使用一次
              </p>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || code.length < 8}
            >
              {isLoading ? '验证中...' : '验证'}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/2fa/verify?tempToken=${tempToken}`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              使用验证器
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
